/**
 * The HR responses as the platform sends them, checked, and turned into the
 * console's names. `routers/hr.py` and `hr/saudi.py` are the source.
 *
 * The shapes are the domain's (`domain/hr.ts`) with snake_case keys, so the
 * mapping is a key rename, done once here; the schemas check the fields a
 * screen would silently misread if they were missing.
 */
import { z } from "zod";
import type { HrCost, HrLabour, HrPeople, HrRules, HrRulesPatch, HrRulesView, HrSaudization } from "../domain/hr";
import type { HrAttendanceRow, HrRecord, HrStaffFile } from "../domain/hrFile";

const camelKey = (k: string) => k.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
const snakeKey = (k: string) => k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

function rekey(v: unknown, f: (k: string) => string): unknown {
  if (Array.isArray(v)) return v.map((x) => rekey(x, f));
  if (v && typeof v === "object")
    return Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([k, x]) => [f(k), rekey(x, f)]));
  return v;
}

export const camel = <T>(v: unknown): T => rekey(v, camelKey) as T;
export const snake = (v: unknown): unknown => rekey(v, snakeKey);

const num = z.number();
const numN = z.number().nullable();

export const wirePeople = z.object({
  employees: z.array(
    z
      .object({
        id: z.string(),
        employee_no: z.string(),
        name: z.string(),
        nationality: z.string(),
        employment: z.enum(["direct", "outsourced"]),
        status: z.enum(["active", "on_leave", "left"]),
        hired_on: z.string(),
        basic: num,
        housing: num,
        saudi: z.boolean(),
      })
      .passthrough(),
  ),
  users: z.array(z.object({ id: z.string(), display_name: z.string() }).passthrough()),
  departments: z.array(z.string()),
  trades: z.array(z.string()),
  rules_basis: z.enum(["default", "entered"]),
});

const wireRules = z
  .object({
    as_of: z.string(),
    basis: z.enum(["default", "entered"]),
    gosi: z.object({ base_min: num, base_max: num, hazards_employer: num }).passthrough(),
    eosb: z.object({ first_years: num, wage: z.string() }).passthrough(),
    nitaqat: z.object({ full_at_wage: num, target_pct: numN }).passthrough(),
  })
  .passthrough();

export const wireRulesView = z.object({
  rules: wireRules,
  defaults: z.object({}).passthrough(),
  restated: z.object({}).passthrough(),
  updated_at: z.string().nullable(),
  updated_by: z.string().nullable(),
});

export const wireCost = z
  .object({
    as_of: z.string(),
    lines: z.array(
      z.object({ employee_id: z.string(), monthly_cost: num, hourly_rate: numN, costed: z.boolean() }).passthrough(),
    ),
    totals: z.object({ monthly: num, annual: num, heads: num }).passthrough(),
    coverage: z.object({ people: num, costed: num }).passthrough(),
    method: z.string(),
  })
  .passthrough();

export const wireLabour = z
  .object({
    coverage: z.object({ work_orders: num, with_hours: num, by_person: num, by_trade: num }).passthrough(),
    totals: z.object({ hours: num, cost: num }),
    work_orders: z.array(z.object({ id: z.string(), hours: num, cost: numN }).passthrough()),
    plans: z.object({ annual_cost: num }).passthrough(),
    method: z.string(),
  })
  .passthrough();

export const wireSaudization = z
  .object({
    heads: num,
    counted: num,
    expats: num,
    pct: numN,
    target_pct: numN,
    hires_needed: numN,
    method: z.string(),
  })
  .passthrough();

export const mapPeople = (w: z.infer<typeof wirePeople>) => camel<HrPeople>(w);
export const mapRulesView = (w: z.infer<typeof wireRulesView>) => {
  const v = camel<HrRulesView>(w);
  return { ...v, rules: v.rules as HrRules, defaults: v.defaults as HrRules, restated: v.restated as HrRulesPatch };
};
export const mapCost = (w: z.infer<typeof wireCost>) => camel<HrCost>(w);
export const mapSaudization = (w: z.infer<typeof wireSaudization>) => camel<HrSaudization>(w);

/**
 * `overtime_12m` → `overtime12m`, `overtime_hours_12m` → `overtimeHours12m`:
 * the digit keys camelise the same way as the rest, so no special case.
 */
export const mapLabour = (w: z.infer<typeof wireLabour>) => camel<HrLabour>(w);

/* ---- the staff file ------------------------------------------------------------- */

/** `data` is the record's own free-form content: its keys are not renamed. */
export function mapRecord(raw: unknown): HrRecord {
  const r = raw as Record<string, unknown>;
  const { data, ...rest } = r;
  return { ...camel<Omit<HrRecord, "data">>(rest), data: (data as Record<string, unknown>) ?? {} } as HrRecord;
}

/** Postgres times come back as "07:40:00"; the screens show "07:40". */
export const trimTimes = <T extends { checkIn?: string | null; checkOut?: string | null }>(a: T): T => ({
  ...a,
  checkIn: a.checkIn ? a.checkIn.slice(0, 5) : null,
  checkOut: a.checkOut ? a.checkOut.slice(0, 5) : null,
});

export const wireRecords = z.object({
  records: z.array(z.object({ id: z.string(), kind: z.string(), status: z.string(), employee_id: z.string() }).passthrough()),
  confidential_hidden: z.boolean(),
});

export const mapRecords = (w: z.infer<typeof wireRecords>) => ({
  records: w.records.map(mapRecord),
  confidentialHidden: w.confidential_hidden,
});

export const wireFile = z.object({
  employee: z.object({ id: z.string(), name: z.string(), hired_on: z.string() }).passthrough(),
  photo: z.string().nullable(),
  records: z.array(z.object({ id: z.string() }).passthrough()),
  attendance: z.array(z.object({ day: z.string(), status: z.string() }).passthrough()),
  cost: z.object({}).passthrough().nullable(),
  can: z.object({ pay: z.boolean(), confidential: z.boolean(), write: z.boolean(), approve: z.boolean() }),
  confidential_hidden: z.boolean(),
});

export function mapFile(w: z.infer<typeof wireFile>): HrStaffFile {
  const { emergency_contact, ...emp } = w.employee as Record<string, unknown>;
  return {
    employee: { ...camel<HrStaffFile["employee"]>(emp), emergencyContact: (emergency_contact as HrStaffFile["employee"]["emergencyContact"]) ?? null },
    photo: w.photo,
    records: w.records.map(mapRecord),
    attendance: camel<HrAttendanceRow[]>(w.attendance).map(trimTimes),
    cost: w.cost ? camel<HrStaffFile["cost"]>(w.cost) : null,
    can: w.can,
    confidentialHidden: w.confidential_hidden,
  };
}
